import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useCachedProducts from "../hooks/UseCachedProducts";
import { LuSearch, LuPackage } from "react-icons/lu";

function SearchAutoComplete({ onSelect }) {
  const [search, setSearch]                     = useState("");
  const [suggestions, setSuggestions]           = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef    = useRef(null);
  const navigate        = useNavigate();
  const { user: currentUser } = useAuth();
  const allProducts     = useCachedProducts();

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value.toLowerCase();
    setSearch(q);
    if (!q.trim()) { setSuggestions([]); return; }
    setSuggestions(allProducts.filter(p => p.name?.toLowerCase().includes(q)).slice(0, 5));
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") setHighlightedIndex(p => Math.min(p + 1, suggestions.length - 1));
    else if (e.key === "ArrowUp") setHighlightedIndex(p => Math.max(p - 1, 0));
    else if (e.key === "Enter" && highlightedIndex >= 0) {
      const sel = suggestions[highlightedIndex];
      if (sel) { navigate(`/products/details/${sel.id}`); onSelect?.(); setSearch(""); setSuggestions([]); }
    }
  };

  const handleClick = (item) => {
    navigate(`/products/details/${item.id}`);
    onSelect?.();
    setSearch("");
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <div className="flex items-center bg-muted border border-border rounded-full px-4 py-1.5 gap-2">
        <LuSearch size={16} className="text-[var(--color-muted-foreground)] flex-shrink-0" />
        <input
          type="text"
          value={search}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Search wholesale inventory..."
          className="bg-transparent border-none text-sm text-foreground placeholder:text-[var(--color-muted-foreground)] focus:outline-none w-full"
        />
      </div>
      {suggestions.length > 0 && (
        <ul className="absolute z-40 bg-card border border-border w-full mt-2 rounded-2xl shadow-2xl overflow-hidden" style={{ minWidth: "340px" }}>
          {suggestions.map((item, idx) => (
            <li
              key={item.id}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onClick={() => handleClick(item)}
              className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors ${idx === highlightedIndex ? "bg-accent/10" : "hover:bg-muted"} ${idx !== 0 ? "border-t border-border" : ""}`}
            >
              <div className="w-11 h-11 rounded-xl overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center">
                {item.imageUrls?.[0] ? (
                  <img src={item.imageUrls[0]} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <LuPackage size={20} className="text-[var(--color-muted-foreground)]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground text-sm truncate">{item.name}</p>
                {item.category && <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">{item.category}</p>}
              </div>
              {currentUser && item.price != null && (
                <span className="text-xs font-black text-foreground flex-shrink-0">${Number(item.price).toFixed(2)}</span>
              )}
            </li>
          ))}
          <li className="px-4 py-2 bg-muted border-t border-border flex items-center justify-between">
            <span className="text-xs text-[var(--color-muted-foreground)]">{suggestions.length} result{suggestions.length !== 1 ? "s" : ""}</span>
            <span className="text-xs text-[var(--color-muted-foreground)]">↑↓ navigate · Enter to open</span>
          </li>
        </ul>
      )}
    </div>
  );
}

export default SearchAutoComplete;
